import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Search, ChevronRight, ArrowRight, Phone, CheckCircle, Users } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }
  })
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const AnimSection = ({ children, className, style }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} variants={stagger} initial="hidden" animate={inView ? 'visible' : 'hidden'} className={className} style={style}>
      {children}
    </motion.div>
  );
};

const Counter = ({ target, suffix, label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0; const step = target / 60;
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 25);
    return () => clearInterval(timer);
  }, [inView, target]);
  return (
    <motion.div ref={ref} variants={fadeUp} style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 5vw, 4rem)', color: 'var(--orange)', letterSpacing: '1px', lineHeight: 1 }}>
        {count.toLocaleString('fr')}{suffix}
      </div>
      <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.4rem', fontWeight: 500 }}>{label}</div>
    </motion.div>
  );
};

const services = [
  { icon: '', label: 'Mécanique Moto', desc: 'Révision, moteur, freins, carburation', color: '#FF6B00', key: 'MECANIQUE_MOTO' },
  { icon: '', label: 'Mécanique Voiture', desc: 'Vidange, embrayage, distribution', color: '#1A3B5D', key: 'MECANIQUE_VOITURE' },
  { icon: '', label: 'Vulcanisateur', desc: 'Pneus, équilibrage, gonflage', color: '#10B981', key: 'VULCANISATEUR' },
  { icon: '', label: 'Électricité Auto', desc: 'Batterie, alternateur, câblage', color: '#F59E0B', key: 'ELECTRICITE_AUTO' },
  { icon: '', label: 'Carrosserie', desc: 'Débosselage, soudure, peinture', color: '#8B5CF6', key: 'CARROSSERIE' },
  { icon: '', label: 'Climatisation', desc: 'Recharge gaz, compresseur', color: '#06B6D4', key: 'CLIMATISATION' },
];

const steps = [
  { icon: <Search size={28} />, title: 'Cherchez', desc: 'Choisissez votre service et ville. Filtrez par note, prix ou disponibilité.' },
  { icon: <Users size={28} />, title: 'Comparez', desc: 'Consultez profils, photos de réalisations, avis et tarifs en toute transparence.' },
  { icon: <Phone size={28} />, title: 'Contactez', desc: 'Envoyez un message ou appelez directement via la plateforme sécurisée.' },
  { icon: <CheckCircle size={28} />, title: 'Payez', desc: 'Réglez via Mobile Money, Moov Money, Celtis Cash.' },
];

const ParticleField = () => {
  const particles = Array.from({ length: 25 }, (_, i) => ({
    id: i, x: Math.random() * 100, y: Math.random() * 100,
    size: Math.random() * 3 + 1, duration: Math.random() * 8 + 4, delay: Math.random() * 4,
  }));
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {particles.map(p => (
        <motion.div key={p.id}
          animate={{ y: [0, -25, 0], opacity: [0.2, 0.7, 0.2] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', left: `${p.x}%`, top: `${p.y}%`,
            width: p.size, height: p.size, borderRadius: '50%',
            background: 'rgba(255,107,0,0.7)',
            boxShadow: `0 0 ${p.size * 4}px rgba(255,107,0,0.4)`,
          }} />
      ))}
    </div>
  );
};

export default function HomePage() {
  const [searchService, setSearchService] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchService) params.set('service', searchService);
    if (searchCity) params.set('city', searchCity);
    navigate(`/prestataires?${params.toString()}`);
  };

  return (
    <div style={{ background: '#080E18' }}>

      {/* ══ HERO ══ */}
      <section ref={heroRef} style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>

        {/* BG IMAGE */}
        <motion.div style={{ position: 'absolute', inset: '-10%', scale: imgScale }}>
          <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=90" alt="bg"
            style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.2) saturate(1.3)' }} />
        </motion.div>

        {/* OVERLAYS */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg, rgba(8,14,24,0.97) 0%, rgba(8,14,24,0.82) 45%, rgba(255,107,0,0.06) 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(255,107,0,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(255,107,0,0.04) 1px,transparent 1px)', backgroundSize: '80px 80px' }} />

        <ParticleField />

        {/* GEARS */}
        {[{ size: 500, top: '-15%', right: '-8%', spd: 50, op: 0.03 }, { size: 200, bottom: '5%', left: '-3%', spd: -28, op: 0.05 }].map((g, i) => (
          <motion.div key={i} animate={{ rotate: g.spd > 0 ? 360 : -360 }} transition={{ duration: Math.abs(g.spd), repeat: Infinity, ease: 'linear' }}
            style={{ position: 'absolute', fontSize: g.size, lineHeight: 1, top: g.top, bottom: g.bottom, left: g.left, right: g.right, color: `rgba(255,107,0,${g.op})`, pointerEvents: 'none' }}>⚙</motion.div>
        ))}

        {/* CONTENT */}
        <motion.div style={{ opacity: heroOpacity, y: heroY, position: 'relative', zIndex: 10, width: '100%' }}>
          <div className="container">
            <div className="hero-grid">

              {/* LEFT TEXT */}
              <div className="hero-left">
                <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.3)', borderRadius: '100px', padding: '0.4rem 1rem', marginBottom: '1.5rem' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--orange)', display: 'inline-block' }} />
                    <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)', letterSpacing: '3px', fontWeight: 600 }}>🇧🇯 PLATEFORME N°1 AU BÉNIN</span>
                  </div>
                </motion.div>

                {/* TITRE */}
                <div className="hero-title">
                  {[
                    { text: 'VOTRE', delay: 0.1, orange: false },
                    { text: 'MÉCANIQUE', delay: 0.25, orange: true },
                    { text: 'SIMPLIFIÉE', delay: 0.45, orange: false },
                  ].map((line, li) => (
                    <div key={li}>
                      <motion.span
                        initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: line.delay, ease: [0.16, 1, 0.3, 1] }}
                        style={{
                          display: 'inline-block',
                          ...(line.orange ? { background: 'linear-gradient(90deg, #FF6B00, #FF9A3C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } : {}),
                        }}
                      >{line.text}</motion.span>
                    </div>
                  ))}
                </div>

                <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}
                  style={{ fontSize: '1rem', lineHeight: '1.85', color: 'rgba(255,255,255,0.55)', marginBottom: '2rem' }}>
                  La panne en pleine circulation n'est plus un stress. ELIM DÉPANNAGE vous rejoint où que vous soyez et peu importe l'heure.
                </motion.p>

                {/* SEARCH */}
                <motion.form initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }}
                  onSubmit={handleSearch} className="search-form">
                  <select value={searchService} onChange={e => setSearchService(e.target.value)}
                    style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', color: searchService ? 'white' : 'rgba(255,255,255,0.35)', padding: '0.9rem 0.8rem', fontSize: '0.88rem', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <option value="" style={{ background: '#0D1B2A' }}>🔧 Service</option>
                    {services.map(s => <option key={s.key} value={s.key} style={{ background: '#0D1B2A' }}>{s.icon} {s.label}</option>)}
                  </select>
                  <input value={searchCity} onChange={e => setSearchCity(e.target.value)} placeholder="📍 Votre ville..."
                    style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', color: 'white', padding: '0.9rem 0.8rem', fontSize: '0.88rem' }} />
                  <button type="submit" className="search-btn">
                    <Search size={15} /> CHERCHER
                  </button>
                </motion.form>

                {/* MINI STATS */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
                  style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
                  {[{ val: '450+', label: 'Prestataires' }, { val: '2 800+', label: 'Clients' }, { val: '4.8★', label: 'Note' }, { val: '3', label: 'Villes' }].map((s, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 + i * 0.1 }}
                      style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.7rem', color: 'var(--orange)', lineHeight: 1 }}>{s.val}</div>
                      <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', textTransform: 'uppercase' }}>{s.label}</div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* BOUTON URGENCE */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.3, duration: 0.6 }}
                >
                  <Link to="/inscription" style={{ textDecoration: 'none' }}>
                    <motion.div
                      animate={{ boxShadow: ['0 0 0 0 rgba(239,68,68,0.7)', '0 0 0 20px rgba(239,68,68,0)', '0 0 0 0 rgba(239,68,68,0)'] }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '1rem',
                        background: 'linear-gradient(135deg, #EF4444, #B91C1C)',
                        border: '2px solid rgba(255,255,255,0.15)',
                        borderRadius: '16px', padding: '0.9rem 1.5rem',
                        cursor: 'pointer', userSelect: 'none',
                      }}
                    >
                      <motion.span
                        animate={{ opacity: [1, 0.15, 1] }}
                        transition={{ duration: 0.75, repeat: Infinity }}
                        style={{ fontSize: '1.6rem', lineHeight: 1 }}
                      >
                        🚨
                      </motion.span>
                      <div>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'white', letterSpacing: '2px', lineHeight: 1, marginBottom: '0.25rem' }}>
                          JE SUIS EN PANNE !
                        </div>
                        <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.65)', letterSpacing: '1.5px', fontFamily: 'var(--font-cond)', fontWeight: 600 }}>
                          Trouver un mécanicien maintenant →
                        </div>
                      </div>
                      <motion.div
                        animate={{ scale: [1, 1.5, 1] }}
                        transition={{ duration: 0.75, repeat: Infinity }}
                        style={{ width: 10, height: 10, borderRadius: '50%', background: 'white', flexShrink: 0 }}
                      />
                    </motion.div>
                  </Link>
                </motion.div>

              </div>

              {/* RIGHT IMAGE - desktop only */}
              <motion.div initial={{ opacity: 0, x: 80 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="hero-image-col" style={{ position: 'relative', paddingTop: '9rem', paddingBottom: '2rem' }}>

                <div style={{ position: 'absolute', top: '9%', right: '-2%', width: '84%', height: '78%', border: '1px solid rgba(255,107,0,0.2)', borderRadius: '22px' }} />

                <motion.div animate={{ y: [-8, 8, -8] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ position: 'relative', zIndex: 2 }}>
                  <img src="\mechanic.jpg" alt="Mécanicien"
                    style={{ width: '100%', maxHeight: '530px', objectFit: 'cover', borderRadius: '20px', boxShadow: '0 50px 100px rgba(0,0,0,0.75)', display: 'block' }} />
                  <div style={{ position: 'absolute', inset: 0, borderRadius: '20px', background: 'linear-gradient(to top, rgba(8,14,24,0.55) 0%, transparent 50%)' }} />

                  <motion.div animate={{ y: [-4, 4, -4] }} transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                    style={{ position: 'absolute', bottom: '18px', left: '-22px', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(20px)', borderRadius: '16px', padding: '0.9rem 1.2rem', boxShadow: '0 20px 50px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #FF6B00, #FF9A3C)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>⭐</div>
                    <div>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: '#0D1B2A', lineHeight: 1 }}>4.8 / 5</div>
                      <div style={{ fontSize: '0.65rem', color: '#888', letterSpacing: '1.5px', marginTop: '2px' }}>NOTE MOYENNE</div>
                    </div>
                  </motion.div>

                  <motion.div animate={{ y: [-4, 4, -4] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                    style={{ position: 'absolute', top: '18px', right: '-22px', background: 'linear-gradient(135deg, #FF6B00, #CC4400)', borderRadius: '16px', padding: '0.9rem 1.2rem', boxShadow: '0 20px 50px rgba(255,107,0,0.5)', display: 'flex', alignItems: 'center', gap: '0.8rem', color: 'white' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>✅</div>
                    <div>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', lineHeight: 1 }}>450+</div>
                      <div style={{ fontSize: '0.65rem', opacity: 0.85, letterSpacing: '1.5px', marginTop: '2px' }}>VÉRIFIÉS</div>
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>

            </div>
          </div>
        </motion.div>

        {/* SCROLL INDICATOR */}
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}
          style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', color: 'rgba(255,255,255,0.25)', zIndex: 10 }}>
          <span style={{ fontSize: '0.62rem', letterSpacing: '3px', textTransform: 'uppercase' }}>DÉFILER</span>
          <div style={{ width: '22px', height: '34px', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '11px', display: 'flex', justifyContent: 'center', paddingTop: '5px' }}>
            <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
              style={{ width: '4px', height: '4px', background: 'var(--orange)', borderRadius: '50%' }} />
          </div>
        </motion.div>
      </section>

      {/* ══ URGENCE ══ */}
      <div style={{ background: 'linear-gradient(90deg, #FF6B00, #CC4400)', padding: '1rem 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(45deg,transparent,transparent 10px,rgba(0,0,0,0.06) 10px,rgba(0,0,0,0.06) 20px)' }} />
        <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center', position: 'relative', textAlign: 'center' }}>
          <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }} style={{ fontSize: '1.5rem' }}>⚡</motion.span>
          <strong style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'white', letterSpacing: '2px' }}>PANNE EN URGENCE ?</strong>
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>Des techniciens disponibles 24h/24, 7j/7 dans toutes les grandes villes du Bénin</span>
          <Link to="/prestataires?isAvailable=true" className="btn btn-outline-white btn-sm">AIDE IMMÉDIATE <ArrowRight size={14} /></Link>
        </div>
      </div>

      {/* ══ SERVICES ══ */}
      <section style={{ background: '#0D1420', padding: '5rem 0' }}>
        <div className="container">
          <AnimSection>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <motion.div variants={fadeUp}><span style={{ display: 'inline-block', fontSize: '0.7rem', letterSpacing: '4px', color: 'var(--orange)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '1rem' }}>— NOS MÉTIERS —</span></motion.div>
              <motion.h2 variants={fadeUp} style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 4rem)', color: 'white', letterSpacing: '1px', lineHeight: 1.05 }}>
                TOUS VOS BESOINS<br />
                <span style={{ background: 'linear-gradient(90deg, #FF6B00, #FF9A3C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>MÉCANIQUES COUVERTS</span>
              </motion.h2>
            </div>
            <div className="services-grid">
              {services.map((s, i) => (
                <motion.div key={s.key} variants={fadeUp} custom={i}>
                  <Link to={`/prestataires?service=${s.key}`} style={{ display: 'block', textDecoration: 'none' }}>
                    <motion.div whileHover={{ y: -10, scale: 1.02 }} transition={{ type: 'spring', stiffness: 300 }}
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '18px', padding: '1.5rem', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: '-15px', right: '-15px', fontSize: '80px', opacity: 0.06, lineHeight: 1 }}>{s.icon}</div>
                      <h3 style={{ fontFamily: 'var(--font-cond)', fontSize: '1.1rem', fontWeight: 700, color: 'white', marginBottom: '0.4rem', letterSpacing: '0.5px' }}>{s.label}</h3>
                      <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', lineHeight: '1.6', marginBottom: '1rem' }}>{s.desc}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: s.color, fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '1px' }}>
                        VOIR LES PRESTATAIRES <ChevronRight size={14} />
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ══ NOTRE MISSION ══ */}
      <section style={{ background: '#080E18', padding: '5rem 0', overflow: 'hidden' }}>
        <div className="container">
          <AnimSection>
            <div className="mission-grid">

              {/* IMAGE */}
              <motion.div variants={fadeUp} style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '25px', left: '25px', width: '100%', height: '100%', border: '1px solid rgba(255,107,0,0.18)', borderRadius: '22px', zIndex: 0 }} />
                <motion.div animate={{ y: [-6, 6, -6] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }} style={{ position: 'relative', zIndex: 1 }}>
                  <img src="\mec.jpg" alt="Mécanicien professionnel"
                    style={{ width: '100%', borderRadius: '20px', boxShadow: '0 40px 80px rgba(0,0,0,0.7)', maxHeight: '480px', objectFit: 'cover', display: 'block' }} />
                  <div style={{ position: 'absolute', inset: 0, borderRadius: '20px', background: 'linear-gradient(to top, rgba(8,14,24,0.5) 0%, transparent 50%)' }} />

                  <motion.div animate={{ y: [-3, 3, -3] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                    style={{ position: 'absolute', bottom: '20px', left: '-18px', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(20px)', borderRadius: '14px', padding: '0.8rem 1.2rem', boxShadow: '0 15px 40px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                    <span style={{ fontSize: '1.4rem' }}>⭐</span>
                    <div>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: '#0D1B2A', lineHeight: 1 }}>4.8/5</div>
                      <div style={{ fontSize: '0.65rem', color: '#888', letterSpacing: '1px' }}>NOTE MOYENNE</div>
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* TEXTE */}
              <motion.div variants={fadeUp} custom={1} style={{ color: 'white' }}>
                <span style={{ fontSize: '0.7rem', letterSpacing: '4px', color: 'var(--orange)', fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: '1rem' }}>— NOTRE MISSION —</span>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 4vw, 3.2rem)', letterSpacing: '1px', lineHeight: 1.05, margin: '0 0 1.5rem' }}>
                  CONNECTER LES BÉNINOIS<br />
                  <span style={{ background: 'linear-gradient(90deg, #FF6B00, #FF9A3C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>À DES MÉCANICIENS<br />DE CONFIANCE</span>
                </h2>
                <p style={{ opacity: 0.55, lineHeight: 1.9, marginBottom: '2rem', fontSize: '1rem' }}>
                  ELIM DEPANNAGE naît d'un constat simple : trouver un bon mécanicien au Bénin relevait du bouche-à-oreille. Nous avons changé ça.
                </p>
                {[
                  { icon: '🔍', text: 'Prestataires vérifiés et notés par de vrais clients' },
                  { icon: '📍', text: 'Disponibles dans 3 grandes villes du Bénin' },
                  { icon: '⚡', text: 'Mise en relation en moins de 2 minutes' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.9rem', padding: '0.8rem 1rem', borderRadius: '10px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                    <span style={{ opacity: 0.7, fontSize: '0.92rem' }}>{item.text}</span>
                  </div>
                ))}
                <div style={{ marginTop: '2rem' }}>
                  <Link to="/inscription" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', background: 'linear-gradient(135deg, #FF6B00, #CC4400)', color: 'white', padding: '1rem 2rem', borderRadius: '10px', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.9rem', letterSpacing: '1.5px', textDecoration: 'none', boxShadow: '0 10px 30px rgba(255,107,0,0.35)' }}>
                    REJOINDRE LA PLATEFORME <ArrowRight size={18} />
                  </Link>
                </div>
              </motion.div>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ══ HOW IT WORKS ══ */}
      <section style={{ background: '#0D1420', padding: '5rem 0' }}>
        <div className="container">
          <AnimSection>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <motion.div variants={fadeUp}><span style={{ fontSize: '0.7rem', letterSpacing: '4px', color: 'var(--orange)', fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: '1rem' }}>— SIMPLE & RAPIDE —</span></motion.div>
              <motion.h2 variants={fadeUp} style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 4rem)', color: 'white', letterSpacing: '1px' }}>
                COMMENT ÇA <span style={{ background: 'linear-gradient(90deg, #FF6B00, #FF9A3C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>FONCTIONNE</span>
              </motion.h2>
            </div>
            <div className="steps-grid">
              {steps.map((step, i) => (
                <motion.div key={i} variants={fadeUp} custom={i}>
                  <motion.div whileHover={{ y: -8 }} transition={{ type: 'spring', stiffness: 300 }}
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '18px', padding: '2rem 1.5rem', textAlign: 'center', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #FF6B00, #CC4400)', color: 'white', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: '0.9rem', boxShadow: '0 4px 15px rgba(255,107,0,0.5)' }}>{i + 1}</div>
                    <div style={{ width: '65px', height: '65px', borderRadius: '50%', background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--orange)', margin: '0 auto 1.2rem' }}>{step.icon}</div>
                    <h3 style={{ fontFamily: 'var(--font-cond)', fontSize: '1.2rem', fontWeight: 700, color: 'white', marginBottom: '0.7rem', letterSpacing: '1px' }}>{step.title}</h3>
                    <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.42)', lineHeight: '1.7' }}>{step.desc}</p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ══ STATS ══ */}
      <section style={{ background: '#080E18', padding: '4rem 0', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="container">
          <AnimSection>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '2rem' }}>
              <Counter target={450} suffix="+" label="Prestataires inscrits" />
              <Counter target={2800} suffix="+" label="Clients satisfaits" />
              <Counter target={6} suffix="" label="Villes couvertes" />
              <Counter target={98} suffix="%" label="Taux de satisfaction" />
            </div>
          </AnimSection>
        </div>
      </section>

     {/* ══ CARTE COUVERTURE ══ */}
<section style={{ background: '#0D1420', padding: '5rem 0' }}>
  <div className="container">
    <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
      <span style={{ fontSize: '0.7rem', letterSpacing: '4px', color: 'var(--orange)', fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: '1rem' }}>— ZONES DE SERVICE —</span>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 4vw, 3.2rem)', color: 'white', letterSpacing: '1px', marginBottom: '0.8rem' }}>
        ON EST LÀ <span style={{ background: 'linear-gradient(90deg, #FF6B00, #FF9A3C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>POUR VOUS</span>
      </h2>
      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.95rem' }}>Service actif dans le sud du Bénin — D'autres villes arrivent bientôt</p>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' }} className="map-grid">

      {/* COLONNE GAUCHE — infos */}
      <div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'white', letterSpacing: '1px', marginBottom: '1.5rem' }}>
          ELIM DEPANNAGE DISPONIBLE DANS<br />
          <span style={{ color: 'var(--orange)' }}>5 VILLES</span>
        </h3>

        {/* Villes actives */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', marginBottom: '2rem' }}>
          {[
            { name: 'Cotonou',        desc: 'Capitale économique' },
            { name: 'Porto-Novo',     desc: 'Capitale officielle' },
            { name: 'Abomey-Calavi',  desc: 'Zone Atlantique' },
            { name: 'Godomey',        desc: 'Entre Cotonou et Abomey-Calavi' },
            { name: 'Akassato',       desc: 'Zone périurbaine ouest' },
          ].map((v, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 1.2rem', background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                <motion.div
                  animate={{ opacity: [1, 0.2, 1], scale: [1, 1.4, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.4 }}
                  style={{ width: 9, height: 9, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 8px #10B981', flexShrink: 0 }}
                />
                <div>
                  <div style={{ color: 'white', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.95rem' }}>{v.name}</div>
                  <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem', marginTop: '0.1rem' }}>{v.desc}</div>
                </div>
              </div>

            </motion.div>
          ))}

          {/* Bientôt */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.9rem 1.2rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px' }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
            <div>
              <div style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.9rem' }}>Parakou, Bohicon, Natitingou...</div>
              <div style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.72rem', marginTop: '0.1rem' }}>🚀 Bientôt disponible</div>
            </div>
          </div>
        </div>

        {/* Stat rapide */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
          {[
            { val: '5', label: 'Villes couvertes' },
            { val: '24h', label: 'Disponibilité' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.15)', borderRadius: 10, padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--orange)', lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.3rem', fontFamily: 'var(--font-cond)', letterSpacing: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* COLONNE DROITE — image carte */}
      <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,107,0,0.2)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>

        <img src="\carte.png" alt="Carte Bénin"
          style={{ width: '100%', display: 'block', filter: 'brightness(0.65) saturate(0.7)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,14,24,0.2)' }} />

        {/* Badge */}
        <div style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(8,14,24,0.9)', border: '1px solid rgba(255,107,0,0.3)', borderRadius: 8, padding: '0.4rem 0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          <motion.div animate={{ opacity: [1, 0.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }}
            style={{ width: 7, height: 7, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 6px #10B981' }} />
          <span style={{ fontSize: '0.68rem', color: '#10B981', fontFamily: 'var(--font-cond)', fontWeight: 700, letterSpacing: 1 }}>5 VILLES ACTIVES</span>
        </div>

        {/* Label bas */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(8,14,24,0.95), transparent)', padding: '2rem 1.2rem 1rem' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'white', letterSpacing: '2px' }}>SUD DU BÉNIN</div>
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.2rem' }}>Zone de couverture actuelle</div>
        </div>
      </div>

    </div>
  </div>
</section>

      {/* ══ GALERIE ══ */}
      <section style={{ overflow: 'hidden' }}>
        <div className="gallery-grid">
          {['\mot1.png', '\mecmot.png', '\Gemini_Generated_Image_j5abwzj5abwzj5ab.png', '\car.png'].map((src, i) => (
            <motion.div key={i} whileHover={{ scale: 1.06 }} style={{ overflow: 'hidden', position: 'relative' }}>
              <img src={src} alt={`Mécanique ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(8,14,24,0.7), transparent)' }} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* ══ PAIEMENT ══ */}
      <section style={{ background: '#0D1420', padding: '5rem 0' }}>
        <div className="container">
          <AnimSection>
            <div className="payment-grid">
              <motion.div variants={fadeUp}>
                <span style={{ fontSize: '0.7rem', letterSpacing: '4px', color: 'var(--orange)', fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: '1rem' }}>— PAIEMENT SÉCURISÉ —</span>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 4vw, 3.2rem)', color: 'white', letterSpacing: '1px', marginBottom: '1.5rem', lineHeight: 1.05 }}>
                  PAYEZ COMME<br /><span style={{ background: 'linear-gradient(90deg, #FF6B00, #FF9A3C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>VOUS VOULEZ</span>
                </h2>
                <p style={{ opacity: 0.5, lineHeight: 1.9, marginBottom: '2rem', fontSize: '0.98rem', color: 'white' }}>Notre système intègre tous les moyens de paiement populaires au Bénin. Transactions sécurisées SSL.</p>
                {['🔒 Transactions cryptées SSL 256-bit', '📱 Confirmation instantanée par SMS', '↩️ Remboursement garanti sous 48h', '📊 Historique complet de vos paiements'].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.9rem' }}>
                    <CheckCircle size={16} style={{ color: '#10B981', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)' }}>{f}</span>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeUp} custom={1}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
                  {[
                    { logo: '/mtn-momo.png', name: 'Mobile Money', sub: 'MTN Bénin', color: '#FFC107', bg: 'rgba(255,193,7,0.08)' },
                    { logo: '/moov-money.png', name: 'Moov Money', sub: 'Moov Africa', color: '#2196F3', bg: 'rgba(33,150,243,0.08)' },
                    { logo: '/celtiis-cash.png', name: 'Celtiis Cash', sub: 'Celtiis', color: '#1A5276', bg: 'rgba(26,82,118,0.12)' }
                  ].map((p, i) => (
                    <motion.div key={i} whileHover={{ y: -6 }} style={{ background: p.bg, borderRadius: '16px', padding: '1.5rem 1rem', textAlign: 'center', border: `1px solid ${p.color}30` }}>
                      <div style={{ marginBottom: '0.8rem', display: 'flex', justifyContent: 'center' }}>
                        <img src={p.logo} alt={p.name} style={{ width: 64, height: 48, objectFit: 'contain', borderRadius: '8px' }} />
                      </div>
                      <div style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.9rem', color: 'white' }}>{p.name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', marginTop: '0.2rem' }}>{p.sub}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ══ DOUBLE CTA ══ */}
      <section style={{ background: '#080E18', padding: '5rem 0' }}>
        <div className="container">
          <AnimSection>
            <div className="cta-double" style={{ borderRadius: '24px', overflow: 'hidden', boxShadow: '0 40px 100px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <motion.div variants={fadeUp} style={{ padding: '3rem 2rem', background: 'linear-gradient(135deg, #1A3B5D 0%, #0D1B2A 100%)', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '1rem' }}>
                <img src="\client-panne.png" alt="Client en panne" style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '14px', opacity: 0.65 }} />
                <span style={{ fontSize: '2.5rem' }}></span>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem, 3vw, 2rem)', letterSpacing: '1px' }}>VOUS CHERCHEZ<br />UN MÉCANICIEN ?</h3>
                <p style={{ opacity: 0.55, lineHeight: 1.7, maxWidth: '280px', fontSize: '0.9rem' }}>Inscrivez-vous gratuitement et accédez à des centaines de prestataires vérifiés.</p>
                <Link to="/inscription?type=client" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'linear-gradient(135deg, #FF6B00, #CC4400)', color: 'white', padding: '0.9rem 1.8rem', borderRadius: '10px', textDecoration: 'none', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.88rem', letterSpacing: '1px', boxShadow: '0 8px 25px rgba(255,107,0,0.35)' }}>S'inscrire comme Client →</Link>
              </motion.div>
              <motion.div variants={fadeUp} custom={1} style={{ padding: '3rem 2rem', background: 'linear-gradient(135deg, #CC4400 0%, #FF6B00 100%)', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '1rem' }}>
                <img src="\mecagarage.png" alt="Mécanicien" style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '14px', opacity: 0.65 }} />
                <span style={{ fontSize: '2.5rem' }}></span>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem, 3vw, 2rem)', letterSpacing: '1px' }}>VOUS ÊTES<br />MÉCANICIEN ?</h3>
                <p style={{ opacity: 0.8, lineHeight: 1.7, maxWidth: '280px', fontSize: '0.9rem' }}>Créez votre profil, montrez vos réalisations et développez votre clientèle.</p>
                <Link to="/inscription?type=prestataire" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.45)', color: 'white', padding: '0.9rem 1.8rem', borderRadius: '10px', textDecoration: 'none', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.88rem', letterSpacing: '1px' }}>Rejoindre comme Prestataire →</Link>
              </motion.div>
            </div>
          </AnimSection>
        </div>
      </section>

      <style>{`
        /* HERO */
        .hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
          padding: 5rem 0;
        }
        .hero-left { padding: 0; }
        .hero-title {
          font-family: var(--font-display);
          font-size: clamp(3rem, 7vw, 7.5rem);
          line-height: 0.88;
          letter-spacing: 1px;
          color: white;
          margin-bottom: 1.5rem;
        }
        .hero-image-col { display: none; }
        @media (min-width: 1024px) {
          .hero-image-col { display: block !important; }
        }

        /* SEARCH FORM */
        .search-form {
          display: flex;
          flex-direction: column;
          background: rgba(255,255,255,0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 14px;
          overflow: hidden;
          margin-bottom: 2rem;
          box-shadow: 0 20px 60px rgba(0,0,0,0.4);
        }
        .search-form select,
        .search-form input {
          border-bottom: 1px solid rgba(255,255,255,0.08) !important;
          border-right: none !important;
        }
        .search-btn {
          background: linear-gradient(135deg, #FF6B00, #CC4400);
          border: none;
          color: white;
          padding: 0.9rem 1rem;
          cursor: pointer;
          font-size: 0.82rem;
          font-weight: 700;
          letter-spacing: 1.5px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          font-family: var(--font-cond);
        }

        @media (min-width: 640px) {
          .search-form {
            flex-direction: row;
          }
          .search-form select {
            border-bottom: none !important;
            border-right: 1px solid rgba(255,255,255,0.08) !important;
          }
          .search-form input {
            border-bottom: none !important;
          }
        }

        /* SERVICES */
        .services-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }
        @media (min-width: 768px) {
          .services-grid { grid-template-columns: repeat(3, 1fr); }
        }

        /* MISSION */
        .mission-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          align-items: center;
        }
        @media (min-width: 768px) {
          .mission-grid { grid-template-columns: 1fr 1fr; gap: 5rem; }
        }

        /* STEPS */
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .steps-grid { grid-template-columns: repeat(4, 1fr); }
        }

        /* MAP */
        .map-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          align-items: center;
        }
        @media (min-width: 768px) {
          .map-grid { grid-template-columns: 1fr 1.6fr; gap: 3rem; }
        }

        /* GALLERY */
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          height: 240px;
        }
        @media (min-width: 768px) {
          .gallery-grid { grid-template-columns: repeat(4, 1fr); height: 280px; }
        }

        /* PAYMENT */
        .payment-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          align-items: center;
        }
        @media (min-width: 768px) {
          .payment-grid { grid-template-columns: 1fr 1fr; gap: 5rem; }
        }

        /* CTA DOUBLE */
        .cta-double {
          display: grid;
          grid-template-columns: 1fr;
        }
        @media (min-width: 768px) {
          .cta-double { grid-template-columns: 1fr 1fr; }
        }

        /* HERO responsive */
        @media (max-width: 1023px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            padding: 6rem 0 3rem;
          }
        }
        @media (max-width: 480px) {
          .hero-title { font-size: clamp(2.8rem, 12vw, 4rem); }
        }
      `}</style>

    </div>
  );
}
